import { ImpressionsList, ImpressionsItem, ImageSize } from '../types/types';

export interface PreviewImages {
    nextUrl: string;
    nextSrcset?: string;
    prevUrl: string;
    prevSrcset?: string;
    currentIndex: number;
}

/**
 * Map image size to width descriptor for srcset
 * Uses actual image widths from backend:
 * - MOBILE: 640px
 * - TABLET: 1024px
 * - DESKTOP: 1280px
 * - ORIGINAL: typically larger, using 1920w as estimate
 * @param size - Image size enum value
 * @returns Width descriptor string (e.g., "640w") or empty string if size is unknown
 */
const getWidthDescriptor = (size: ImageSize): string => {
    const widthMap: Record<ImageSize, string> = {
        MOBILE: '640w',
        TABLET: '1024w',
        DESKTOP: '1280w',
        ORIGINAL: '1920w', // Estimate - actual size may vary
    };
    return widthMap[size] || '';
};

/**
 * Build srcset string with width descriptors
 * Browser will automatically select the best image based on viewport and device pixel ratio
 * Note: ORIGINAL is excluded from srcset - it should only be used as fallback in src attribute
 * @param images - Array of images with size and url
 * @returns Formatted srcset string (e.g., "url1 640w, url2 1024w, url3 1280w")
 */
export function buildSrcset(images: Array<{ size: ImageSize; url: string }>): string {
    if (!images || images.length === 0) {
        return '';
    }

    // Filter out ORIGINAL images - they should only be used as fallback in src attribute
    // Sort remaining images by size order: MOBILE -> TABLET -> DESKTOP
    const filteredImages = images.filter((img) => img.size !== 'ORIGINAL');

    if (filteredImages.length === 0) {
        return '';
    }

    const sortedImages = [...filteredImages].sort((a, b) => {
        const order: Record<ImageSize, number> = {
            MOBILE: 1,
            TABLET: 2,
            DESKTOP: 3,
            ORIGINAL: 4,
        };
        return (order[a.size] || 99) - (order[b.size] || 99);
    });

    return sortedImages
        .map((img) => {
            const width = getWidthDescriptor(img.size);
            return width ? `${img.url} ${width}` : img.url;
        })
        .join(', ');
}

/**
 * Get preview image - always uses MOBILE size since preview images are only 72px
 * MOBILE (640px) is more than enough even for Retina displays (72px * 2 = 144px)
 * @param item - Impression item
 * @returns Object with URL (MOBILE) and no srcset needed
 */
function getPreviewImage(item: ImpressionsItem): { url: string; srcset?: string } {
    // Preview images are only 72px, so MOBILE (640px) is always sufficient
    // Even on Retina displays (2x DPR), 72px * 2 = 144px, which is well below 640px
    if (item.images && item.images.length > 0) {
        // Find MOBILE image
        const mobileImage = item.images.find((img) => img.size === 'MOBILE');
        if (mobileImage) {
            return {
                url: mobileImage.url,
                // No srcset needed - MOBILE is always sufficient for 72px preview images
            };
        }
        // Fallback to first available image if MOBILE not found
        return {
            url: item.images[0].url,
        };
    }

    // Fallback to default img if no images array
    return {
        url: item.img || '',
    };
}

/**
 * Utility functions for slider preview image calculations
 */
export class SliderUtils {
    /**
     * Calculate preview image URLs and current index
     * @param currentIndex - Current slide index
     * @param impressionsList - List of impressions
     * @returns Object with preview URLs and current index
     */
    static calculatePreviewImages(currentIndex: number, impressionsList: ImpressionsList | undefined): PreviewImages {
        if (!impressionsList || !impressionsList.items || impressionsList.items.length === 0) {
            return {
                nextUrl: '',
                prevUrl: '',
                currentIndex: 0,
            };
        }

        const listItems = impressionsList.items;
        const listLength = listItems.length;

        const nextIndex = (currentIndex + 1) % listLength;
        const prevIndex = (currentIndex - 1 + listLength) % listLength;

        const nextImage = getPreviewImage(listItems[nextIndex]);
        const prevImage = getPreviewImage(listItems[prevIndex]);

        return {
            nextUrl: nextImage.url,
            nextSrcset: nextImage.srcset,
            prevUrl: prevImage.url,
            prevSrcset: prevImage.srcset,
            currentIndex: currentIndex,
        };
    }
}
